import moment from "moment";

export const marketName = process.env.REACT_APP_COMPLIA_INSTALL;
const isElevatedEnv = /-sandbox[.]/.test(window.location.host);
const isLowerEnv =
  /-dev[.]|-development[.]|-test[.]|-qa[.]|-local[.]|localhost/.test(
    window.location.host
  );
const isLocalhost = /localhost/.test(window.location.host);

function getEnvironment() {
  const host = window.location.host.toLowerCase();

  if (host.includes("localhost") || host.includes("-local.")) {
    return "local";
  }
  if (host.includes("-dev.") || host.includes("-development.")) {
    return "development";
  }
  if (host.includes("-qa.")) {
    return "qa";
  }
  if (host.includes("-sandbox.")) {
    return "sandbox";
  }

  return "production";
}

function parseUrlParam(str) {
  let queryObj = {};
  try {
    queryObj = JSON.parse(str);
  } catch (err) {
    return queryObj;
  }
  return queryObj;
}
/* drill down through data return final value */
function getPropertyValue(data, path) {
  // support for mapping multiple properties
  if (!path || !data) return "";
  if (path.indexOf(",") > 0) {
    return path.split(",").reduce((acc, cur) => {
      if (cur && cur.indexOf(".") > 0) {
        const [objName, propName] = cur.split(".");
        const val = data[objName]?.[propName] ?? "";
        return acc ? `${acc} ${val}` : val;
      }
      const val = data[cur] ?? "";
      return acc ? `${acc} ${val}` : val;
    }, "");
  }
  if (path.indexOf(".") === -1) return data[path];
  const parts = path.split(".");
  let value = data;
  if (parts.length > 1) {
    parts.forEach((part) => {
      if (typeof value === "object") {
        value = value[part];
      }
    });
  }
  return typeof value === "object" ? "" : value;
}

function getDisplayValue(column, value, data) {
  const { aliases, aliasMap } = column || {};

  if (aliases?.[value]) return aliases[value];

  if (aliasMap?.[value]) {
    const { label, conditional } = aliasMap[value];
    if (!conditional) return label || value;
    const matchesWorkflow = conditional.eq?.includes(
      getPropertyValue(data, conditional.when)
    );
    return matchesWorkflow ? label || value : value;
  }

  return value;
}

function getformattedCellValue(value, condition, format, rowsInfo) {
  if (!format || !rowsInfo[condition]) return value;

  let formattedValue = format;
  let hasDynamicVariable = true;
  while (hasDynamicVariable) {
    const startIndex = formattedValue.indexOf("{{");
    if (startIndex === -1 || startIndex === undefined) {
      hasDynamicVariable = false;
      break;
    }
    const endIndex = formattedValue.indexOf("}}");
    const dynamicVariable = formattedValue.substring(startIndex + 2, endIndex);
    formattedValue = formattedValue.replace(
      `{{${dynamicVariable}}}`,
      rowsInfo[dynamicVariable]
    );
  }
  return formattedValue;
}

/* construct app navigation url based on data and config */
function getNavLink(data, config) {
  if (!data || !config.linkTo) return null;
  const parts = config.linkTo.split("/");
  if (parts.length < 3) return config.linkTo;
  let navLink = "";
  parts.forEach((part) => {
    if (part) {
      if (part.indexOf(":") === -1) {
        navLink = navLink === "" ? `/${part}/` : `${navLink}${part}/`;
      } else {
        navLink =
          navLink === ""
            ? `/${data[part.substring(part, 1)]}/`
            : `${navLink}${data[part.substring(1)]}/`;
      }
    }
  });
  return navLink;
}

function notifyError(dispatch, showNotification, error) {
  const msg =
    error?.data?.message && typeof error.data?.message === "string"
      ? error.data.message
      : "Could not process request, please try later.";
  dispatch(showNotification({ type: "error", msg, show: true }));
}

const getValue = (field, defaultValues, searchParams, splitVal) => {
  const value = searchParams?.get?.(field);
  if (value) return splitVal ? value.split(",") : value;
  if (Array.isArray(defaultValues) && !splitVal) return defaultValues.join(",");
  return defaultValues;
};

function getAppliedFilters(listConfig, searchParams = {}) {
  const { filters, columns } = listConfig || {};
  const defaultProps = {
    page: searchParams?.get?.("page") || 1,
    limit: searchParams?.get?.("limit") || 10,
    sort: searchParams?.get?.("sort") || "",
  };
  const reqInitState = { ...defaultProps };
  const payload = { ...defaultProps };
  filters?.common?.forEach((filter) => {
    const { field, defaultValues } = filter || {};
    if (field.indexOf(".") === -1) {
      reqInitState[field] = getValue(field, defaultValues, searchParams, true);
      payload[field] =
        getValue(field, defaultValues, searchParams) || undefined;
    } else {
      const props = field.split(".");
      const rootProp = props[0];
      const key = props[1];
      const value = getValue(key, defaultValues, searchParams);
      if (value) {
        payload[rootProp] = { ...(payload[rootProp] || {}), [key]: value };
        reqInitState[key] = getValue(key, defaultValues, searchParams, true);
      }
    }
  });
  columns?.forEach((filter) => {
    if (filter?.defaultSort) {
      const sortBy = filter.isDesc ? `-${filter.sortBy}` : filter.sortBy;
      reqInitState.sort = searchParams?.get?.("sort")
        ? searchParams.get("sort").split(",")
        : sortBy;
      payload.sort = searchParams?.get?.("sort") || sortBy;
    }
  });
  return [reqInitState, payload];
}
function constructUrlObject(q) {
  const stringObject = q;
  if (stringObject.invoiceId?.length !== 4) {
    stringObject.invoiceId = "";
  }
  Object.keys(stringObject).forEach((key) => {
    stringObject[key] = stringObject[key] ? String(stringObject[key]) : "";
  });
  return stringObject;
}

function validatePasswords(pwdA, pwdB, type) {
  if (pwdA !== pwdB) {
    return type === "register"
      ? "Password doesn't match."
      : "Both new password doesn't match.";
  }
  if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/.test(pwdA)) {
    return false;
  }
  return "All passwords must be at least eight characters long and contain at least one uppercase letter, one lowercase letter, and one number.";
}

// for digital card dropdown
function getDisplayTitle(titleMapping, recordType, data) {
  let displayTitle = "";
  const type = titleMapping.find((item) => item.key === recordType);
  if (type && type.title && type.title.length) {
    displayTitle += " (";
    type.title.forEach((item, index) => {
      displayTitle += data[item];
      const separator = index + 1 === type.title.length ? "" : " ";
      displayTitle += separator;
    });
    displayTitle += ")";
  }
  return displayTitle;
}

const getMaskedSocialSecurity = (value) => {
  if (!value) return value;
  const inputValueLength = value.toString().length;
  let maskInputLength = inputValueLength;
  if (inputValueLength > 3) maskInputLength += 1;
  if (inputValueLength > 5) maskInputLength += 1;
  return "XXX-XX-XXXX".slice(0, maskInputLength);
};

const formatSocialSecurity = (value) => {
  if (!value) return value;
  return value
    .replace(/\D/g, "")
    .replace(/^(\d{3})/, "$1-")
    .replace(/-(\d{2})/, "-$1-")
    .replace(/(\d)-(\d{4}).*/, "$1-$2");
};

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const digits = String(value).replace(/\D/g, "");
  if (!digits || digits.length > 10) return value;
  const d = digits.padEnd(10, "_");
  return `(${d.slice(0, 3)})${d.slice(3, 6)}-${d.slice(6)}`;
};

const getUnmaskedSocialSecurity = ({
  fieldProps,
  value,
  isFormatted = true,
}) => {
  if (fieldProps?.isDecrypted === true) {
    if (isFormatted) return formatSocialSecurity(value);
    return value;
  }
  return getMaskedSocialSecurity(value);
};

const getSocialSecurityNumber = ({
  isMasked = true,
  value = "",
  fieldProps = {},
  isFormatted = false,
}) =>
  isMasked
    ? getMaskedSocialSecurity(value)
    : getUnmaskedSocialSecurity({
        fieldProps,
        value,
        isFormatted,
      });

function getGeocodeLatLong(data) {
  if (data && data.geocodes) {
    const geocodeData = data.geocodes.primary;
    if (geocodeData && geocodeData.code === "OK" && geocodeData.location)
      return {
        lat: geocodeData.location.coordinates[1],
        long: geocodeData.location.coordinates[0],
        accuracy: (geocodeData.confidenceLevel || 0) * 100,
      };

    return {
      lat: "No information available.",
      long: "No information available.",
      accuracy: null,
    };
  }
  return null;
}

function convertOunce(input, type, roundingMode, decimalPlaces = 1) {
  const ozToGmFactor = 28.34952;
  if (Number.isNaN(+input)) return input;
  if (type === "OzToMg") {
    const milligramsPerOunce = ozToGmFactor * 1000;
    return (input * milligramsPerOunce).toFixed(1);
  }

  const gms = Number(input) * ozToGmFactor;

  if (roundingMode === "down") {
    const factor = 10 ** decimalPlaces;
    const rounded = Math.floor(gms * factor) / factor;
    return rounded.toFixed(decimalPlaces);
  }

  return gms.toFixed(decimalPlaces);
}

function getLatestApiSelector(state, endpointName, type = "mutations") {
  const queries = state.api[type];
  if (!queries || !endpointName) return null;
  const mutations = Object.values(queries).filter(
    (mutation) => mutation.endpointName === endpointName
  );
  if (!mutations.length) return null;
  return mutations.reduce((accumulator, currentMutation) => {
    if (currentMutation.startedTimeStamp > accumulator.startedTimeStamp)
      return currentMutation;
    return accumulator;
  });
}

function convertNumberToOrdinal(inputNumber) {
  if (typeof inputNumber !== "number") return "Input is not a valid";
  if (inputNumber % 1 !== 0) return "Input is not an integer";
  if (inputNumber < 0) return "Input must be a non-negative integer";

  const lastDigit = inputNumber % 10;
  const lastTwoDigits = inputNumber % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) return `${inputNumber}th`;

  switch (lastDigit) {
    case 1:
      return `${inputNumber}st`;
    case 2:
      return `${inputNumber}nd`;
    case 3:
      return `${inputNumber}rd`;
    default:
      return `${inputNumber}th`;
  }
}

export const redirectToSSO = (_path, data = {}) => {
  let path = `api/oidcLogin?email=${encodeURIComponent(
    data.email
  )}&isPrivateDevice=${!data.isPublicComputer}`;
  if (_path === "logout") path = "api/oidcLogout";
  if (_path === "loginByDomain")
    path = `api/auth/loginByDomain?idp_hint=${data.idpHint}`;
  let url = window.location.origin;
  if (window.location.origin.includes("localhost"))
    url = "http://localhost:4000";
  window.location.href = `${url}/${path}`;
  return null;
};

export const isSSOEnabled =
  process.env.REACT_APP_SSO_ENABLE?.toString() === "true";

export const extractQueryParams = (url = "") => {
  const queryString = url.split("?")[1];
  if (!queryString) return {};
  const searchParams = new URLSearchParams(queryString);
  return Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
    if (!value) return acc;
    const parsedValue = value.includes(",")
      ? value.split(",").map((item) => item.trim())
      : value;
    return { ...acc, [key]: parsedValue };
  }, {});
};

export const isDateExceeded = (date, maxDay) => {
  const _date = moment.utc(date).startOf("day");
  const now = moment.utc().startOf("day");
  const diffDays = now.diff(_date, "days");
  return diffDays >= maxDay;
};

export function findComponentByKey(form, targetKey) {
  function searchComponents(components) {
    if (!Array.isArray(components)) {
      return null;
    }
    const result = components.reduce((found, component) => {
      if (found) return found;
      if (component.key === targetKey) {
        return component;
      }
      if (component.components && Array.isArray(component.components)) {
        const nestedFound = searchComponents(component.components);
        if (nestedFound) return nestedFound;
      }
      if (component.type === "table" && component.rows) {
        const tableFound = component.rows.reduce((rowFound, row) => {
          if (rowFound) return rowFound;
          return row.reduce((cellFound, cell) => {
            if (cellFound) return cellFound;
            if (cell.components) {
              return searchComponents(cell.components);
            }
            return null;
          }, null);
        }, null);
        if (tableFound) return tableFound;
      }
      if (component.columns && Array.isArray(component.columns)) {
        const columnFound = component.columns.reduce((colFound, column) => {
          if (colFound) return colFound;
          if (column.components) {
            return searchComponents(column.components);
          }
          return null;
        }, null);
        if (columnFound) return columnFound;
      }
      return null;
    }, null);
    return result;
  }

  if (form && form.components && targetKey)
    return searchComponents(form.components);
  return null;
}

export {
  isLowerEnv,
  isElevatedEnv,
  isLocalhost,
  getEnvironment,
  parseUrlParam,
  getPropertyValue,
  getDisplayValue,
  getformattedCellValue,
  getNavLink,
  notifyError,
  getAppliedFilters,
  constructUrlObject,
  validatePasswords,
  getDisplayTitle,
  getGeocodeLatLong,
  getMaskedSocialSecurity,
  getUnmaskedSocialSecurity,
  getSocialSecurityNumber,
  formatPhoneNumber,
  convertOunce,
  convertNumberToOrdinal,
  getLatestApiSelector,
};
